<template>
  <div class="content m-5">
    <b-loading is-full-page v-model="isLoading"></b-loading>

    <div class="level">
      <div class="level-left">
        <div class="level-item">
          <h1 class="title is-1">Tag Management</h1>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <b-button type="is-primary" icon-left="content-save" @click="saveAll">
            Save All Changes
          </b-button>
        </div>
      </div>
    </div>

    <p class="mb-5 has-text-grey">
      Manage tag categories and individual tags. Changes affect how recipes and meal plans can be tagged.
    </p>

    <!-- Add new category -->
    <div class="box mb-5">
      <h2 class="subtitle is-4">Add New Category</h2>
      <div class="columns">
        <div class="column is-4">
          <b-field label="Category Label">
            <b-input v-model="newCategory.label" placeholder="e.g., Cooking Style" />
          </b-field>
        </div>
        <div class="column is-4">
          <b-field label="Category ID">
            <b-input v-model="newCategory.id" placeholder="e.g., cooking_style" />
          </b-field>
        </div>
        <div class="column is-3">
          <b-field label="Applies to">
            <b-select v-model="newCategory.applies_to" multiple>
              <option value="recipes">Recipes</option>
              <option value="meal_plans">Meal Plans</option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-1">
          <b-field label="&nbsp;">
            <b-button type="is-primary" icon-left="plus" @click="addCategory">Add</b-button>
          </b-field>
        </div>
      </div>
    </div>

    <!-- Existing categories -->
    <draggable
      v-model="categories"
      handle=".category-drag-handle"
      @end="onCategoryReorder">
      <div v-for="(category, catIndex) in categories" :key="category.id" class="box mb-4">
        <div class="level">
          <div class="level-left">
            <div class="level-item category-drag-handle" style="cursor: grab;">
              <b-icon icon="drag-vertical" type="is-grey-light"></b-icon>
            </div>
            <div class="level-item">
              <h2 class="subtitle is-4 mb-0">
                <b-input
                  v-model="category.label"
                  placeholder="Category Label"
                  @input="markDirty"
                />
              </h2>
            </div>
            <div class="level-item">
              <b-taglist>
                <b-tag v-if="category.applies_to.includes('recipes')" type="is-info">Recipes</b-tag>
                <b-tag v-if="category.applies_to.includes('meal_plans')" type="is-success">Meal Plans</b-tag>
              </b-taglist>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <b-dropdown aria-role="list" position="is-bottom-left">
                <template #trigger>
                  <b-button icon-right="dots-vertical" />
                </template>
                <b-dropdown-item aria-role="listitem" @click="editCategorySettings(category)">
                  <b-icon size="is-small" icon="cog"></b-icon>
                  Settings
                </b-dropdown-item>
                <b-dropdown-item class="has-text-danger" aria-role="listitem" @click="deleteCategory(catIndex)">
                  <b-icon size="is-small" icon="delete"></b-icon>
                  Delete Category
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </div>

        <!-- Tags within category -->
        <draggable
          v-model="category.tags"
          handle=".tag-drag-handle"
          @end="markDirty"
          class="columns is-multiline mt-3">
          <div
            v-for="(tag, tagIndex) in category.tags"
            :key="tag.id"
            class="column is-one-quarter">
            <div class="is-flex is-align-items-center">
              <span class="tag-drag-handle mr-2" style="cursor: grab;">
                <b-icon icon="drag-vertical" size="is-small" type="is-grey-light"></b-icon>
              </span>
              <b-input
                v-model="tag.label"
                size="is-small"
                expanded
                @input="markDirty"
              />
              <b-button
                type="is-danger"
                size="is-small"
                icon-right="close"
                class="ml-1"
                @click="deleteTag(catIndex, tagIndex)"
              />
            </div>
          </div>
        </draggable>

        <!-- Add new tag to category -->
        <div class="mt-3">
          <b-field grouped>
            <b-input
              v-model="newTagInputs[category.id]"
              placeholder="New tag label"
              size="is-small"
              expanded
              @keyup.native.enter="addTag(category.id, catIndex)"
            />
            <p class="control">
              <b-button
                type="is-light"
                size="is-small"
                icon-left="plus"
                @click="addTag(category.id, catIndex)">
                Add Tag
              </b-button>
            </p>
          </b-field>
        </div>
      </div>
    </draggable>

    <!-- Category settings modal -->
    <b-modal v-model="showCategoryModal" has-modal-card trap-focus>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Category Settings</p>
          <button class="delete" aria-label="close" @click="showCategoryModal = false"></button>
        </header>
        <section class="modal-card-body" v-if="editingCategory">
          <b-field label="Category ID (read-only)">
            <b-input :value="editingCategory.id" disabled />
          </b-field>
          <b-field label="Category Label">
            <b-input v-model="editingCategory.label" />
          </b-field>
          <b-field label="Applies to">
            <b-checkbox-button
              v-model="editingCategory.applies_to"
              native-value="recipes"
              type="is-info">
              Recipes
            </b-checkbox-button>
            <b-checkbox-button
              v-model="editingCategory.applies_to"
              native-value="meal_plans"
              type="is-success">
              Meal Plans
            </b-checkbox-button>
          </b-field>
        </section>
        <footer class="modal-card-foot">
          <b-button @click="showCategoryModal = false">Cancel</b-button>
          <b-button type="is-primary" @click="saveCategorySettings">Save</b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import db from '@/firebase/db'
import { TAG_CATEGORIES } from '@/data/tagCategories'

export default {
  name: 'TagManagementView',
  components: {
    Draggable
  },
  data() {
    return {
      isLoading: true,
      isDirty: false,
      categories: [],
      newCategory: {
        id: '',
        label: '',
        applies_to: ['recipes']
      },
      newTagInputs: {},
      showCategoryModal: false,
      editingCategory: null,
      editingCategoryIndex: null
    }
  },
  created() {
    this.loadCategories()
  },
  methods: {
    async loadCategories() {
      this.isLoading = true
      try {
        const snapshot = await db.collection('tag_categories').orderBy('order').get()

        if (snapshot.empty) {
          // Initialize with default categories from data file
          this.categories = Object.values(TAG_CATEGORIES).map((cat, index) => ({
            ...cat,
            order: index + 1
          }))
          this.$buefy.toast.open({
            type: 'is-info',
            message: 'Loaded default tag categories. Click Save to persist to database.'
          })
        } else {
          this.categories = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
        }

        // Initialize newTagInputs for each category
        this.categories.forEach(cat => {
          this.$set(this.newTagInputs, cat.id, '')
        })
      } catch (error) {
        this.$buefy.toast.open({
          type: 'is-danger',
          message: `Error loading categories: ${error}`
        })
      } finally {
        this.isLoading = false
      }
    },

    markDirty() {
      this.isDirty = true
    },

    onCategoryReorder() {
      // Update order property based on new positions
      this.categories.forEach((cat, index) => {
        cat.order = index + 1
      })
      this.markDirty()
    },

    addCategory() {
      if (!this.newCategory.id || !this.newCategory.label) {
        this.$buefy.toast.open({
          type: 'is-warning',
          message: 'Please fill in both ID and Label'
        })
        return
      }

      // Check for duplicate ID
      if (this.categories.some(c => c.id === this.newCategory.id)) {
        this.$buefy.toast.open({
          type: 'is-danger',
          message: 'A category with this ID already exists'
        })
        return
      }

      this.categories.push({
        id: this.newCategory.id,
        label: this.newCategory.label,
        order: this.categories.length + 1,
        applies_to: [...this.newCategory.applies_to],
        tags: []
      })

      this.$set(this.newTagInputs, this.newCategory.id, '')

      // Reset form
      this.newCategory = {
        id: '',
        label: '',
        applies_to: ['recipes']
      }

      this.saveAll()
    },

    deleteCategory(catIndex) {
      const category = this.categories[catIndex]
      this.$buefy.dialog.confirm({
        title: 'Delete Category',
        message: `Are you sure you want to delete <b>${category.label}</b> and all its tags? This cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.categories.splice(catIndex, 1)
          this.markDirty()
        }
      })
    },

    editCategorySettings(category) {
      this.editingCategory = { ...category, applies_to: [...category.applies_to] }
      this.editingCategoryIndex = this.categories.findIndex(c => c.id === category.id)
      this.showCategoryModal = true
    },

    saveCategorySettings() {
      if (this.editingCategoryIndex !== null && this.editingCategory) {
        this.categories[this.editingCategoryIndex].label = this.editingCategory.label
        this.categories[this.editingCategoryIndex].applies_to = [...this.editingCategory.applies_to]
        this.markDirty()
      }
      this.showCategoryModal = false
      this.editingCategory = null
      this.editingCategoryIndex = null
    },

    addTag(categoryId, catIndex) {
      const label = this.newTagInputs[categoryId]?.trim()
      if (!label) return

      // Generate ID from label
      const id = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')

      // Check for duplicate
      if (this.categories[catIndex].tags.some(t => t.id === id)) {
        this.$buefy.toast.open({
          type: 'is-warning',
          message: 'A tag with this name already exists in this category'
        })
        return
      }

      this.categories[catIndex].tags.push({
        id,
        label,
        order: this.categories[catIndex].tags.length + 1
      })

      this.newTagInputs[categoryId] = ''
      this.saveAll();
    },

    deleteTag(catIndex, tagIndex) {
      this.categories[catIndex].tags.splice(tagIndex, 1)
      this.saveAll();
    },

    async saveAll() {
      this.isLoading = true
      try {
        const batch = db.batch()

        // Update order for all tags within each category
        this.categories.forEach((category, catIndex) => {
          category.order = catIndex + 1
          category.tags.forEach((tag, tagIndex) => {
            tag.order = tagIndex + 1
          })

          const docRef = db.collection('tag_categories').doc(category.id)
          batch.set(docRef, {
            id: category.id,
            label: category.label,
            order: category.order,
            applies_to: category.applies_to,
            tags: category.tags,
            updated_at: new Date()
          })
        })

        await batch.commit()

        this.isDirty = false
        this.$buefy.toast.open({
          type: 'is-success',
          message: 'All categories saved successfully!'
        })
      } catch (error) {
        this.$buefy.toast.open({
          type: 'is-danger',
          message: `Error saving categories: ${error}`
        })
      } finally {
        this.isLoading = false
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty) {
      this.$buefy.dialog.confirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to leave?',
        confirmText: 'Leave',
        type: 'is-warning',
        onConfirm: () => next(),
        onCancel: () => next(false)
      })
    } else {
      next()
    }
  }
}
</script>

<style scoped>
.category-drag-handle:hover,
.tag-drag-handle:hover {
  cursor: grab;
}
</style>
